import React, { useEffect, useRef, useState } from 'react';
import '@photo-sphere-viewer/core/index.css';

interface PanoramaViewerProps {
  imageUrl: string;
  className?: string;
  height?: string;
  onLoadingChange?: (isLoading: boolean) => void;
}

// モジュールキャッシュ（一度ロードしたら再利用）
let cachedViewerModule: any = null;
const getViewerModule = async () => {
  if (!cachedViewerModule) {
    cachedViewerModule = await import('@photo-sphere-viewer/core');
  }
  return cachedViewerModule;
};

// three.js/photo-sphere-viewerの"Failed to fetch"エラーを抑制するグローバルハンドラー
let errorHandlerInstalled = false;
const installErrorHandler = () => {
  if (errorHandlerInstalled) return;
  errorHandlerInstalled = true;

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (reason instanceof TypeError && reason.message === 'Failed to fetch') {
      event.preventDefault();
    }
  });

  window.addEventListener('error', (event) => {
    if (event.message?.includes('Failed to fetch')) {
      event.preventDefault();
    }
  }, true);
};

// 初期化時にエラーハンドラーをインストール
installErrorHandler();

const PanoramaViewer: React.FC<PanoramaViewerProps> = ({ imageUrl, className = '', height = '100%', onLoadingChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentUrlRef = useRef<string | null>(null);

  // ローディング状態が変わったら親に通知
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    if (!imageUrl || imageUrl.trim() === '') {
      setError('画像URLが無効です');
      setIsLoading(false);
      return;
    }

    // 同じURLの場合は何もしない
    if (currentUrlRef.current === imageUrl && viewerRef.current) {
      return;
    }

    const targetUrl = imageUrl;
    currentUrlRef.current = targetUrl;

    // 既存のViewerを破棄
    if (viewerRef.current) {
      try {
        viewerRef.current.destroy();
      } catch (e) {
        // 無視
      }
      viewerRef.current = null;
    }

    setIsLoading(true);
    setError(null);

    // ローディングタイムアウト（30秒）
    const loadingTimeout = setTimeout(() => {
      if (currentUrlRef.current === targetUrl) {
        console.warn('PanoramaViewer: Loading timeout for', targetUrl);
        setError('画像の読み込みがタイムアウトしました');
        setIsLoading(false);
      }
    }, 30000);

    // 即座に初期化（遅延なし）
    const initViewer = async () => {
      if (currentUrlRef.current !== targetUrl || !containerRef.current) return;

      try {
        const module = await getViewerModule();

        // URLが変わっていたら中止
        if (currentUrlRef.current !== targetUrl || !containerRef.current) return;

        const viewer = new module.Viewer({
          container: containerRef.current,
          panorama: targetUrl,
          defaultYaw: 0,
          defaultPitch: 0,
          defaultZoomLvl: 0,
          navbar: ['zoom', 'download', 'fullscreen'],
          loadingImg: undefined,
          loadingTxt: '',
          canvasBackground: '#808080',
        });

        if (currentUrlRef.current === targetUrl) {
          viewerRef.current = viewer;

          viewer.addEventListener('ready', () => {
            if (currentUrlRef.current === targetUrl) {
              clearTimeout(loadingTimeout);
              setIsLoading(false);
            }
          });

          viewer.addEventListener('error', (e: any) => {
            if (currentUrlRef.current !== targetUrl) return;
            clearTimeout(loadingTimeout);
            const msg = e?.message || e?.error?.message || String(e) || '';
            if (msg.includes('Failed to fetch') || msg.includes('aborted')) {
              // Failed to fetchエラーでもローディングは解除する
              setIsLoading(false);
              return;
            }
            setError('パノラマ画像の読み込みに失敗しました');
            setIsLoading(false);
          });
        } else {
          try { viewer.destroy(); } catch (e) { /* 無視 */ }
        }
      } catch (e) {
        if (currentUrlRef.current === targetUrl) {
          clearTimeout(loadingTimeout);
          setError('パノラマビューワーの初期化に失敗しました');
          setIsLoading(false);
        }
      }
    };

    initViewer();

    return () => {
      clearTimeout(loadingTimeout);
      // クリーンアップ時はURLをクリアして処理を中止させる
      if (currentUrlRef.current === targetUrl) {
        currentUrlRef.current = null;
      }
    };
  }, [imageUrl]);

  // アンマウント時のみViewerを破棄
  useEffect(() => {
    return () => {
      currentUrlRef.current = null;
      if (viewerRef.current) {
        try { viewerRef.current.destroy(); } catch (e) { /* 無視 */ }
        viewerRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ width: '100%', height }}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <span className="text-gray-500">パノラマ画像を読み込み中...</span>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <span className="text-red-500">{error}</span>
        </div>
      )}
      <div
        ref={containerRef}
        style={{ width: '100%', height }}
      />
    </div>
  );
};

export default PanoramaViewer;
